/**
 * Boîte à outils — vue 3D orbitale (patrons, sections) : glisser sur la figure
 * pour tourner autour du solide, flèches du clavier, boutons « tourner » et
 * « voir de plus haut / bas ». Le dessin reste en SVG (projection
 * orthographique de `space3d.ts`).
 *
 * Geste et clavier vont dans le même sens : vers la droite = flèche →, vers le
 * haut = flèche ↑ (on voit le solide de plus haut). Au doigt, seul le geste
 * horizontal tourne la figure (`touch-action: pan-y`) : un geste vertical fait
 * défiler la page ; l'inclinaison passe par les boutons.
 */

import { useCallback, useId, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { ChevronDown, ChevronUp, RotateCcw, RotateCw } from 'lucide-react';
import { IconButton } from './controls';
import { camera, clampView, type Camera, type V3, type View } from './space3d';

const STEP = Math.PI / 12; // 15°

/** anneau de focus de la figure (elle se manipule au clavier) */
export const ORBIT_FOCUS_CLASS =
  'outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2';

export function useOrbit(initial: View) {
  const [view, setRaw] = useState<View>(() => clampView(initial));
  const setView = useCallback((v: View) => setRaw(clampView(v)), []);
  const drag = useRef<{ x: number; y: number; v: View; id: number } | null>(null);
  const hintId = useId();

  const onPointerDown = (e: PointerEvent<SVGSVGElement>) => {
    drag.current = { x: e.clientX, y: e.clientY, v: view, id: e.pointerId };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: PointerEvent<SVGSVGElement>) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    setView({
      yaw: d.v.yaw - (e.clientX - d.x) * 0.012,
      pitch: d.v.pitch - (e.clientY - d.y) * 0.012,
    });
  };
  const onPointerUp = (e: PointerEvent<SVGSVGElement>) => {
    if (drag.current?.id === e.pointerId) drag.current = null;
  };
  const onKeyDown = (e: KeyboardEvent<SVGSVGElement>) => {
    const k = e.key;
    if (k === 'ArrowLeft') setView({ ...view, yaw: view.yaw + STEP });
    else if (k === 'ArrowRight') setView({ ...view, yaw: view.yaw - STEP });
    else if (k === 'ArrowUp') setView({ ...view, pitch: view.pitch + STEP });
    else if (k === 'ArrowDown') setView({ ...view, pitch: view.pitch - STEP });
    else return;
    e.preventDefault();
  };

  return {
    view,
    setView,
    hintId,
    cam: camera(view),
    svgProps: {
      tabIndex: 0,
      'aria-describedby': hintId,
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onKeyDown,
      style: { touchAction: 'pan-y' as const },
    },
  };
}

/** boutons pour tourner et incliner sans glisser */
export function OrbitButtons({ orbit }: { orbit: ReturnType<typeof useOrbit> }) {
  const { view, setView, hintId } = orbit;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <IconButton
        label="Tourner vers la gauche"
        onClick={() => setView({ ...view, yaw: view.yaw + STEP })}
      >
        <RotateCcw className="h-5 w-5" aria-hidden />
      </IconButton>
      <IconButton
        label="Tourner vers la droite"
        onClick={() => setView({ ...view, yaw: view.yaw - STEP })}
      >
        <RotateCw className="h-5 w-5" aria-hidden />
      </IconButton>
      <IconButton
        label="Voir de plus haut"
        onClick={() => setView({ ...view, pitch: view.pitch + STEP })}
      >
        <ChevronUp className="h-5 w-5" aria-hidden />
      </IconButton>
      <IconButton
        label="Voir de plus bas"
        onClick={() => setView({ ...view, pitch: view.pitch - STEP })}
      >
        <ChevronDown className="h-5 w-5" aria-hidden />
      </IconButton>
      <span id={hintId} className="text-ink-500 min-w-0 flex-1 text-xs">
        ou glisse sur la figure (au clavier : les flèches)
      </span>
    </div>
  );
}

/** projection écran : centre de la scène au milieu d'une figure carrée de `size` */
export function projector(cam: Camera, center: V3, scale: number, size: number) {
  const c = cam.project(center);
  return (p: V3): [number, number] => {
    const q = cam.project(p);
    return [size / 2 + (q.x - c.x) * scale, size / 2 - (q.y - c.y) * scale];
  };
}

export const pointsAttr = (pts: ReadonlyArray<readonly [number, number]>) =>
  pts.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');

/** couleurs des faces : pastels, avec un trait FONCÉ fixe (même en thème sombre) */
export const FACE_TONES = ['#93c5fd', '#fcd34d', '#86efac', '#f9a8d4', '#c4b5fd', '#fdba74'];
export const FACE_STROKE = '#1f2d55';
